import React, { useState, useMemo, useEffect } from "react";
import { Card, Row, Col, Button, Badge, Table, Dropdown, Modal, Form, Alert, Spinner, Toast, ToastContainer } from "react-bootstrap";
import '../assets/css/sidebar.css';
import { useAuth } from "../hooks/useAuth";
import Footer from "../layouts/Footer";

export default function MonCompteOrganisation() {
  const [activeTab, setActiveTab] = useState("facturation");
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [email, setEmail] = useState("comptes@minerve-tech.com");
  const [searchTerm, setSearchTerm] = useState("");
  const [membresData, setMembresData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validatingUser, setValidatingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  
  const { user, logout } = useAuth(); 

  // Fonction pour récupérer les membres via l'API
  const fetchMembers = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('authToken');
    
    try {
      const response = await fetch(`/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Vérifier la structure de la réponse
      if (result.code === 200 && Array.isArray(result.data)) {
        // Transformer les données de l'API en format compatible avec votre composant
        const formattedMembers = formatMembersData(result.data);
        setMembresData(formattedMembers);
      } else {
        throw new Error(`Réponse API invalide: ${result.message || 'Structure de données incorrecte'}`);
      }
      
    } catch (err) {
      console.error('Erreur lors de la récupération des membres:', err);
      setError(`Impossible de charger les membres: ${err}`);
      
      // Données de démonstration en cas d'erreur
      setMembresData(getDemoData());
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ouvrir le modal de validation
  const openValidationModal = (member) => {
    setSelectedMember(member);
    const currentStatus = member.status || member.account_validation;
    const action = currentStatus === 'OK' ? 'désactiver' : 'activer';
    setValidationMessage(`Êtes-vous sûr de vouloir ${action} le compte de ${member.email} ?`);
    setShowValidationModal(true);
  };

  // Fonction pour ouvrir le modal de suppression
  const openDeleteModal = (member) => {
    setSelectedMember(member);
    setDeleteMessage(`Êtes-vous sûr de vouloir supprimer définitivement le compte de ${member.email} ? Cette action est irréversible.`);
    setShowDeleteModal(true);
  };

  // Fonction pour valider un utilisateur
  const validateUser = async () => {
    if (!selectedMember) return;
    
    const token = localStorage.getItem('authToken');
    const currentStatus = selectedMember.status || selectedMember.account_validation;
    const newStatus = currentStatus === 'OK' ? 'KO' : 'OK';
    
    setValidatingUser(selectedMember.id);
    setShowValidationModal(false);
    
    try {
      const response = await fetch(`/users/${selectedMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: newStatus
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.code === 200) {
        // Mettre à jour l'état local
        setMembresData(prev => prev.map(member => 
          member.id === selectedMember.id 
            ? { 
                ...member, 
                account_validation: newStatus,
                statut: getMemberStatus({ ...member, account_validation: newStatus })
              } 
            : member
        ));
        
        // Afficher le toast de succès
        const action = newStatus === 'OK' ? 'activé' : 'désactivé';
        setToastMessage(`Compte ${action} avec succès !`);
        setShowSuccessToast(true);
        
      } else {
      
        throw new Error(result.message || 'Erreur lors de la validation');
      }
      
    } catch (err) {
      console.error('Erreur lors de la validation:', err);
      setToastMessage(`Erreur: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setValidatingUser(null);
      setSelectedMember(null);
    }
  };

  // Fonction pour supprimer un utilisateur
  const deleteUser = async () => {
    if (!selectedMember) return;
    
    setDeletingUser(selectedMember.id);
    setShowDeleteModal(false);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/stops/${selectedMember.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.code === 200) {
        // Supprimer le membre de l'état local
        setMembresData(prev => prev.filter(member => member.id !== selectedMember.id));
        
        // Afficher le toast de succès
        setToastMessage(`Membre ${selectedMember.email} supprimé avec succès !`);
        setShowSuccessToast(true);
        
      } else {
        throw new Error(result.message || 'Erreur lors de la suppression');
      }
      
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setToastMessage(`Erreur lors de la suppression: ${err.message}`);
      setShowErrorToast(true);
    } finally {
      setDeletingUser(null);
      setSelectedMember(null);
    }
  };

  // Fonction pour formater les données de l'API
  const formatMembersData = (apiDataArray) => {
    if (!Array.isArray(apiDataArray)) {
      return getDemoData();
    }
    
    return apiDataArray.map((member, index) => ({
      id: member.id || index + 1,
      email: member.business_email || member.email || `membre${index + 1}@minerve-tech.com`,
      statut: getMemberStatus(member),
      role: mapRole(member.acc_type, member.is_system),
      derniereConnexion: formatLastLogin(member.updated_at),
      nomComplet: `${member.firstname || ''} ${member.lastname || ''}`.trim(),
      phone: member.phone,
      business_name: member.business_name,
      kyc: member.kyc,
      account_validation: member.account_validation,
      status: member.status,
      actions: ["Valider/Invalider", "Supprimer"],
      isCurrentUser: member.id === user?.id
    }));
  };

  // Déterminer le statut du membre
  const getMemberStatus = (memberData) => {
    if (memberData.account_validation === 'OK' && memberData.kyc === 'OK') {
      return "ACTIF";
    }
    return "INACTIF";
  };

  // Fonction pour mapper les rôles
  const mapRole = (accType, isSystem) => {
    if (isSystem === 1) return "Système";
    
    const roleMap = {
      'business': 'Administrateur',
      'admin': 'Administrateur',
      'technician': 'Technicien',
      'commercial': 'Commercial',
      'user': 'Utilisateur'
    };
    return roleMap[accType] || 'Utilisateur';
  };

  // Fonction pour formater la dernière connexion
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return "Non mentioné";
    
    try {
      const date = new Date(lastLogin);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return "Date invalide";
    }
  };

  // Données de démonstration (fallback)
  const getDemoData = () => [
    {
      id: 1017,
      email: "therance.mabounda@minerve-tech.com",
      statut: "ACTIF",
      role: "Administrateur",
      derniereConnexion: "Jamais connecté",
      nomComplet: "Thérance MABOUNDA",
      phone: "+24166640807",
      business_name: "MABTHE",
      kyc: "OK",
      account_validation: "OK",
      status: "OK",
      actions: ["Modifier le rôle", "Valider/Invalider", "Renvoyer l'invitation", "Supprimer"],
      isCurrentUser: true
    }
  ];

  // Charger les membres au montage du composant
  useEffect(() => {
    fetchMembers();
  }, [user?.id]);

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === '0000-00-00 00:00:00') {
      return 'Non définie';
    }
    
    try {
      const date = new Date(dateTimeString);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '');
      
    } catch (error) {
      console.error('Erreur de formatage date/heure:', error);
      return 'Erreur date';
    }
  };

  const organisationData = {
    nom: `${user?.business_name}`,
    verification: user?.kyc === 'OK' ? 100 : 60,
    dateCreation: `${formatDateTime(user?.reg_date)}`,
    telephone: `${user?.phone}`,
    siren: `${user?.business_id || user?.id}`,
    email: `${user?.business_email}`,
    responsable: `${user?.firstname} ${user?.lastname}`
  };

  const facturationData = {
    adresse: [
      user?.business_name || '',
      user?.business_city || 'Ville non définie',
      user?.business_country || 'Pays non défini',
      user?.business_id ? `SIREN: ${user.business_id}` : "ND"
    ],
    email: user?.business_email
  };

  // Filtrer les membres basé sur la recherche
  const filteredMembres = useMemo(() => {
    if (!searchTerm.trim()) return membresData;
    
    const term = searchTerm.toLowerCase();
    return membresData.filter(membre => 
      membre.email.toLowerCase().includes(term) ||
      membre.role.toLowerCase().includes(term) ||
      membre.statut.toLowerCase().includes(term) ||
      membre.derniereConnexion.toLowerCase().includes(term) ||
      (membre.nomComplet && membre.nomComplet.toLowerCase().includes(term)) ||
      (membre.business_name && membre.business_name.toLowerCase().includes(term))
    );
  }, [membresData, searchTerm]);

  // Compter les membres actifs
  const activeMembersCount = useMemo(() => {
    return filteredMembres.filter(membre => membre.statut === "ACTIF").length;
  }, [filteredMembres]);

  const handleAddMember = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const role = formData.get('role');
    
   
    const newMember = {
      id: Date.now(),
      email: email,
      statut: "INACTIF",
      role: role,
      derniereConnexion: "Invitation envoyée",
      nomComplet: "",
      phone: "",
      business_name: "",
      kyc: "PENDING",
      account_validation: "PENDING",
      status: "PENDING",
      actions: ["Modifier le rôle", "Valider/Invalider", "Renvoyer l'invitation", "Supprimer"],
      isCurrentUser: false
    };
    
    setMembresData(prev => [...prev, newMember]);
    setShowAddMemberModal(false);
  };

  const handleEditOrganisation = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const business_name = formData.get('business_name');
    const business_email = formData.get('business_email');
    const phone = formData.get('phone');
    const business_id = formData.get('business_id');
    const responsable = formData.get('responsable');
    

    
    setShowEditOrgModal(false);
  };

  // Gestionnaire de recherche
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Réinitialiser la recherche
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Recharger les membres
  const handleReloadMembers = () => {
    fetchMembers();
  };

  // Gérer les actions sur les membres
  const handleMemberAction = (memberId, action) => {
    
    const member = membresData.find(m => m.id === memberId);
    
    switch (action) {
      case "Modifier le rôle":
        const nouveauRole = prompt("Nouveau rôle (Administrateur, Technicien, Commercial, Utilisateur):");
        if (nouveauRole) {
          setMembresData(prev => prev.map(m => 
            m.id === memberId ? { ...m, role: nouveauRole } : m
          ));
        }
        break;
        
      case "Valider/Invalider":
        if (member) {
          openValidationModal(member);
        }
        break;
        
      case "Renvoyer l'invitation":
        setToastMessage(`Invitation renvoyée à ${member?.email}`);
        setShowSuccessToast(true);
        break;
        
      case "Supprimer":
        if (member) {
          if (member.isCurrentUser) {
            setToastMessage("Vous ne pouvez pas supprimer votre propre compte.");
            setShowErrorToast(true);
          } else {
            openDeleteModal(member);
          }
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="main main-app p-3 p-lg-4 bg-light">
      {/* Toast de succès */}
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showSuccessToast} 
          onClose={() => setShowSuccessToast(false)} 
          delay={5000} 
          autohide
          bg="success"
        >
          <Toast.Header>
            <i className="bi bi-check-circle-fill text-success me-2"></i>
            <strong className="me-auto">Succès</strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>

        {/* Toast d'erreur */}
        <Toast 
          show={showErrorToast} 
          onClose={() => setShowErrorToast(false)} 
          delay={5000} 
          autohide
          bg="danger"
        >
          <Toast.Header>
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            <strong className="me-auto">Erreur</strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Bandeau bleu d'information */}
      <Alert variant="info" className="py-2 mb-4 border-0 text-center fw-semibold">
        <a href="#" className="text-decoration-underline fw-semibold text-dark">
          Remplissez les détails de votre organisation
        </a>{" "}
        afin d'accéder à plus de services.
      </Alert>

      {/* Alert d'erreur */}
      {error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <div>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
          <Button variant="outline-danger" size="sm" onClick={handleReloadMembers}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Réessayer
          </Button>
        </Alert>
      )}

      <Row className="g-4">
        {/* Colonne des onglets - col-3 */}
        <Col lg={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4 d-flex flex-column">
              <h4 className="fw-bold mb-3 text-dark">Compte organisation</h4>
              <div className="tabs-vertical flex-grow-1">
                {[
                  { 
                    id: "organisation", 
                    label: "Organisation", 
                    icon: "bi-building" 
                  },
                  { 
                    id: "membres", 
                    label: "Membres", 
                    icon: "bi-people" 
                  },
                  { 
                    id: "facturation", 
                    label: "Facturation", 
                    icon: "bi-receipt" 
                  }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className={`tab-vertical w-100 text-start p-3 mb-2 border-0 rounded-3 ${
                      activeTab === tab.id ? "active" : "text-dark"
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <div className="d-flex align-items-center">
                      <i className={`${tab.icon} me-3 fs-6`}></i>
                      <div>
                        <div className="fw-semibold">{tab.label}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Colonne du contenu - col-9 */}
        <Col lg={9}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              
              {/* === ORGANISATION === */}
              {activeTab === "organisation" && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold mb-0 text-dark">Informations sur l'organisation</h4>
                    {/* <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowEditOrgModal(true)}
                      className="d-flex align-items-center gap-2"
                    >
                      <i className="bi bi-pencil"></i> Éditer
                    </Button> */}
                  </div>

                  <Row className="g-4">
                    {/* Colonne gauche */}
                    <Col md={5}>
                      <Card className="border-0 bg-light h-100">
                        <Card.Body className="p-4">
                          <div className="d-flex flex-column align-items-center text-center">
                            <div
                              className="org-logo bg-success text-white fw-bold rounded-circle d-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: "100px",
                                height: "100px",
                                fontSize: "2rem",
                              }}
                            >
                              {user?.business_name ? user.business_name.substring(0, 2).toUpperCase() : 'MT'}
                            </div>
                            <h5 className="fw-bold text-dark mb-2">{organisationData.nom}</h5>
                            {user?.kyc && user?.kyc === 'OK' && (
                              <>
                                <Badge bg="success" className="mb-2">
                                  Vérifiée à {organisationData.verification}%
                                </Badge>
                                <div className="w-100">
                                  <div className="progress mb-3" style={{height: "6px"}}>
                                    <div 
                                      className="progress-bar bg-success" 
                                      style={{width: `${organisationData.verification}%`}}
                                    ></div>
                                  </div>
                                </div>
                                <div className="alert alert-success small mb-0">
                                  <i className="bi bi-check-circle me-2"></i>
                                  Organisation entièrement vérifiée
                                </div>
                              </>
                            )}
                            {(!user?.kyc || user?.kyc !== 'OK') && (
                              <div className="alert alert-warning small mb-0">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                Vérification en attente
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Colonne droite */}
                    <Col md={7}>
                      <Card className="border-0 h-100">
                        <Card.Body className="p-4">
                          <h6 className="fw-bold text-dark mb-3">Détails de l'organisation</h6>
                          <div className="org-details">
                            {[
                              { label: "Date de création", value: organisationData.dateCreation },
                              { label: "Numéro de téléphone", value: organisationData.telephone },
                              { label: "Identifiant", value: organisationData.siren },
                              { label: "Email", value: organisationData.email },
                              { label: "Responsable", value: organisationData.responsable },
                              { label: "Pays", value: user?.business_country || 'Non défini' },
                              // { label: "Statut KYC", value: user?.kyc === 'OK' ? 'Validé' : 'En attente' },
                              // { label: "Validation compte", value: user?.account_validation === 'OK' ? 'Validé' : 'En attente' }
                            ].map((item, index) => (
                              <div key={index} className="mb-3 pb-2 border-bottom">
                                <div className="text-muted small mb-1">{item.label}</div>
                                <div className="fw-semibold text-dark">{item.value}</div>
                              </div>
                            ))}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}

              {/* === MEMBRES === */}
              {activeTab === "membres" && (
                <>
                  {/* En-tête amélioré */}
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                          <i className="bi bi-people-fill text-primary fs-5"></i>
                        </div>
                        <div>
                          <h4 className="fw-bold mb-1 text-dark">Membres de l'organisation</h4>
                          <p className="text-muted mb-0">
                            Gérez les accès et permissions des collaborateurs
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Button 
                        variant="outline-primary" 
                        onClick={handleReloadMembers}
                        disabled={loading}
                        className="d-flex align-items-center gap-2"
                      >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                        Actualiser
                      </Button>
                      {/* <Button 
                        variant="primary" 
                        onClick={() => setShowAddMemberModal(true)}
                        className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
                      >
                        <i className="bi bi-person-plus"></i>
                        Ajouter un membre
                      </Button> */}
                    </div>
                  </div>

                  {/* Indicateur de chargement */}
                  {loading && (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" className="me-2" />
                      <span className="text-muted">Chargement des membres...</span>
                    </div>
                  )}

                  {/* Carte principale améliorée */}
                  {!loading && (
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="p-0">
                        {/* En-tête de la table avec statistiques */}
                        <div className="p-4 border-bottom bg-light rounded-top">
                          <div className="row align-items-center">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center gap-4">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary bg-opacity-10 p-2 rounded me-2">
                                    <i className="bi bi-people text-primary"></i>
                                  </div>
                                  <div>
                                    <div className="fw-semibold text-dark">Total des membres</div>
                                    <div className="h4 fw-bold text-primary mb-0">{filteredMembres.length}</div>
                                  </div>
                                </div>
                                <div className="vr"></div>
                                <div className="d-flex align-items-center">
                                  <div className="bg-success bg-opacity-10 p-2 rounded me-2">
                                    <i className="bi bi-check-circle text-success"></i>
                                  </div>
                                  <div>
                                    <div className="fw-semibold text-dark">Membres actifs</div>
                                    <div className="h4 fw-bold text-success mb-0">{activeMembersCount}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6 text-end">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                <div className="position-relative" style={{maxWidth: '250px'}}>
                                  <Form.Control 
                                    placeholder="Rechercher un membre..." 
                                    className="border-0 bg-white shadow-sm ps-5"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    style={{paddingRight: searchTerm ? '40px' : '12px'}}
                                  />
                                  <i className="bi bi-search position-absolute start-0 top-50 translate-middle-y text-muted ms-3"></i>
                                  {searchTerm && (
                                    <button
                                      className="btn btn-sm position-absolute end-0 top-50 translate-middle-y text-muted me-2 border-0 bg-transparent"
                                      onClick={handleClearSearch}
                                      style={{width: '20px', height: '20px'}}
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  )}
                                </div>
                                <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-2">
                                  <i className="bi bi-filter"></i>
                                  Filtres
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Table améliorée */}
                        <div className="table-responsive">
                          <Table hover className="align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="border-0 ps-4 fw-semibold text-dark">Membre</th>
                                <th className="border-0 fw-semibold text-dark">Statut</th>
                                <th className="border-0 fw-semibold text-dark">Rôle</th>
                                <th className="border-0 fw-semibold text-dark">Dernière connexion</th>
                                <th className="border-0 pe-4 fw-semibold text-dark text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredMembres.length > 0 ? (
                                filteredMembres.map((membre) => (
                                  <tr key={membre.id} className="border-bottom">
                                    <td className="ps-4">
                                      <div className="d-flex align-items-center">
                                        <div className="position-relative">
                                          <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                                               style={{width: '40px', height: '40px', fontSize: '1rem', color: 'white'}}>
                                            {membre.email.charAt(0).toUpperCase()}
                                          </div>
                                          {membre.isCurrentUser && (
                                            <div className="position-absolute top-0 start-100 translate-middle">
                                              <Badge bg="info" className="px-1" style={{fontSize: '0.6rem'}}>
                                                Vous
                                              </Badge>
                                            </div>
                                          )}
                                          <div className={`position-absolute bottom-0 end-0 rounded-circle border border-2 border-white ${
                                            membre.statut === "ACTIF" ? 'bg-success' : 'bg-secondary'
                                          }`}
                                               style={{width: '12px', height: '12px'}}></div>
                                        </div>
                                        <div>
                                          <div className="fw-semibold text-dark">{membre.email}</div>
                                          {membre.nomComplet && (
                                            <small className="text-muted">{membre.nomComplet}</small>
                                          )}
                                          {membre.business_name && membre.business_name !== user?.business_name && (
                                            <small className="text-muted d-block">{membre.business_name}</small>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column gap-1">
                                        <Badge 
                                          bg={membre.statut === "ACTIF" ? "success" : "secondary"} 
                                          className="px-3 py-2 d-flex align-items-center gap-1" 
                                          style={{width: 'fit-content'}}
                                        >
                                          <i className={`bi ${membre.statut === "ACTIF" ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                                          {membre.statut}
                                        </Badge>
                                        <Badge 
                                          bg={membre.status === 'OK' || membre.account_validation === 'OK' ? "success" : "warning"} 
                                          className="px-2 py-1 small"
                                        >
                                          {membre.status === 'OK' || membre.account_validation === 'OK' ? 'Validé' : 'En attente'}
                                        </Badge>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-check text-primary"></i>
                                        <Badge 
                                          bg={membre.role === "Administrateur" ? "primary" : "outline-primary"} 
                                          text={membre.role === "Administrateur" ? "white" : "dark"}
                                          className="border px-3 py-2"
                                        >
                                          {membre.role}
                                        </Badge>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-clock-history text-muted"></i>
                                        <span className="text-dark fw-semibold">{membre.derniereConnexion}</span>
                                      </div>
                                    </td>
                                    <td className="pe-4 text-end position-relative">
                                      <div className="dropdown">
                                        <button
                                          className="btn btn-light border-0 rounded-circle d-flex align-items-center justify-content-center"
                                          style={{width: '32px', height: '32px'}}
                                          disabled={validatingUser === membre.id || deletingUser === membre.id}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const dropdown = e.currentTarget.nextElementSibling;
                                            dropdown.classList.toggle('show');
                                            
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            dropdown.style.position = 'fixed';
                                            dropdown.style.left = `${rect.left - 150}px`;
                                            dropdown.style.top = `${rect.bottom + 5}px`;
                                          }}
                                        >
                                          {validatingUser === membre.id || deletingUser === membre.id ? (
                                            <Spinner animation="border" size="sm" />
                                          ) : (
                                            <i className="bi bi-three-dots-vertical"></i>
                                          )}
                                        </button>
                                        <div className="dropdown-menu shadow-lg border-0" style={{minWidth: '200px'}}>
                                          {membre.actions.map((action, i) => (
                                            <button 
                                              key={i}
                                              className="dropdown-item d-flex align-items-center gap-2 py-2 border-0 bg-transparent w-100 text-start"
                                              onClick={() => handleMemberAction(membre.id, action)}
                                              disabled={
                                                (membre.isCurrentUser && action === "Supprimer") || 
                                                validatingUser === membre.id ||
                                                deletingUser === membre.id
                                              }
                                            >
                                              {action === "Modifier le rôle" && <i className="bi bi-person-gear text-primary"></i>}
                                              {action === "Valider/Invalider" && (
                                                <i className={`bi ${membre.status === 'OK' || membre.account_validation === 'OK' ? 'bi-x-circle text-warning' : 'bi-check-circle text-success'}`}></i>
                                              )}
                                              {action === "Renvoyer l'invitation" && <i className="bi bi-send-check text-warning"></i>}
                                              {action === "Supprimer" && <i className="bi bi-person-x text-danger"></i>}
                                              {action}
                                              {membre.isCurrentUser && action === "Supprimer" && (
                                                <small className="text-muted ms-1">(non disponible)</small>
                                              )}
                                              {(validatingUser === membre.id && action === "Valider/Invalider") && (
                                                <Spinner animation="border" size="sm" className="ms-1" />
                                              )}
                                              {(deletingUser === membre.id && action === "Supprimer") && (
                                                <Spinner animation="border" size="sm" className="ms-1" />
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center py-5">
                                    <div className="text-muted">
                                      <i className="bi bi-people display-4 d-block mb-3"></i>
                                      <h5>Aucun membre trouvé</h5>
                                      <p>
                                        {searchTerm 
                                          ? `Aucun membre ne correspond à votre recherche "${searchTerm}"`
                                          : "Aucun membre n'est actuellement dans votre organisation"
                                        }
                                      </p>
                                      {searchTerm ? (
                                        <Button 
                                          variant="outline-primary" 
                                          size="sm"
                                          onClick={handleClearSearch}
                                          className="mt-2"
                                        >
                                          Afficher tous les membres
                                        </Button>
                                      ) : (
                                        <Button 
                                          variant="primary" 
                                          size="sm"
                                          onClick={() => setShowAddMemberModal(true)}
                                          className="mt-2"
                                        >
                                          <i className="bi bi-person-plus me-1"></i>
                                          Ajouter un membre
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {/* Pied de table avec pagination */}
                        <div className="p-4 border-top bg-light rounded-bottom">
                          <div className="row align-items-center">
                            <div className="col-md-6">
                              <span className="text-muted">
                                Affichage de <strong>1</strong> à <strong>{filteredMembres.length}</strong> sur {filteredMembres.length} membres
                                {searchTerm && (
                                  <span className="ms-2 text-primary">
                                    (Filtrés sur {membresData.length} au total)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <Button variant="outline-primary" size="sm" disabled>
                                  <i className="bi bi-chevron-left"></i>
                                </Button>
                                <Button variant="primary" size="sm" className="px-3">1</Button>
                                <Button variant="outline-primary" size="sm" disabled>
                                  <i className="bi bi-chevron-right"></i>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Section d'information */}
                  {!loading && (
                    <div className="row mt-4">
                      <div className="col-md-4">
                        <Card className="border-0 bg-warning bg-opacity-10">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-info-circle text-warning fs-5 me-2"></i>
                              <div>
                                <small className="fw-semibold text-dark">Gestion des rôles</small>
                                <div className="text-muted small">Configurez les permissions par rôle</div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4">
                        <Card className="border-0 bg-info bg-opacity-10">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-clock-history text-info fs-5 me-2"></i>
                              <div>
                                <small className="fw-semibold text-dark">Validation des comptes</small>
                                <div className="text-muted small">Activez/désactivez les accès utilisateurs</div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4">
                        <Card className="border-0 bg-success bg-opacity-10">
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-shield-check text-success fs-5 me-2"></i>
                              <div>
                                <small className="fw-semibold text-dark">Sécurité</small>
                                <div className="text-muted small">Protocoles d'accès sécurisés</div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Le reste du code pour la facturation reste inchangé */}
               {activeTab === "facturation" && (
                              <>
                                <h4 className="fw-bold mb-4 text-dark">Facturation</h4>
              
                               <Row className="g-4">
                <Col md={6}>
                  <Card className="border-0 h-100 shadow-sm-hover">
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center">
                          <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                            <i className="bi bi-geo-alt-fill text-primary fs-5"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0 text-dark">Adresse de facturation</h6>
                            <small className="text-muted">Adresse principale</small>
                          </div>
                        </div>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="border-1 rounded-circle d-flex align-items-center justify-content-center"
                          style={{width: '32px', height: '32px'}}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </div>
                      
                      <div className="flex-grow-1 d-flex align-items-center">
                        <div className="text-muted">
                          {/* Ligne 1 - Ville et Pays */}
                          {facturationData.adresse[0] && (
                            <div className="d-flex align-items-center mb-2">
                              <i className="bi bi-building me-2 text-primary"></i>
                              <strong className="text-dark fs-6">{facturationData.adresse[0]}</strong>
                            </div>
                          )}
                          
                          {/* Ligne 2 - Code postal et ville */}
                          {facturationData.adresse[1] && (
                            <div className="d-flex align-items-center mb-1">
                              <i className="bi bi-geo me-2 text-muted"></i>
                              <span>{facturationData.adresse[1]}</span>
                            </div>
                          )}
                          
                          {/* Ligne 3 - Pays (code) */}
                          {facturationData.adresse[2] && (
                            <div className="d-flex align-items-center mb-1">
                              <i className="bi bi-pin-map me-2 text-muted"></i>
                              <span>{facturationData.adresse[2]}</span>
                            </div>
                          )}
                          
                          {/* Ligne 4 - Autre information */}
                          {facturationData.adresse[3] && (
                            <div className="d-flex align-items-center">
                              <i className="bi bi-postcard me-2 text-muted"></i>
                              <span>{facturationData.adresse[3]}</span>
                            </div>
                          )}
                        </div>
                      </div>
              
                      <div className="mt-3 pt-3">
                        <small className="text-success">
                          <i className="bi bi-check-circle-fill me-1"></i>
                          Adresse vérifiée
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              
                <Col md={6}>
                  <Card className="border-0 h-100 shadow-sm-hover">
                    <Card.Body className="p-4 d-flex flex-column">
                      {/* En-tête avec icône */}
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3">
                          <i className="bi bi-envelope-fill text-warning fs-5"></i>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0 text-dark">Contact de facturation</h6>
                          <small className="text-muted">Email principal</small>
                        </div>
                      </div>
              
                      {/* Description */}
                      <div className="alert alert-light border-0 small mb-4">
                        <i className="bi bi-info-circle me-2 text-info"></i>
                        Les factures seront envoyées chaque mois à cette adresse email.
                      </div>
              
                      {/* Formulaire */}
                      <div className="flex-grow-1">
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-semibold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-at text-primary"></i>
                            Email de facturation *
                          </Form.Label>
                          <div className="input-group">
                            <span className="input-group-text bg-light border-0">
                              <i className="bi bi-envelope text-muted"></i>
                            </span>
                            <Form.Control 
                              type="email" 
                              value={facturationData.email || ''}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="exemple@votre-entreprise.com"
                              className="border-0 bg-light py-3"
                            />
                          </div>
                          <Form.Text className="text-muted small">
                            <i className="bi bi-shield-check me-1"></i>
                            Nous protégeons vos données de facturation
                          </Form.Text>
                        </Form.Group>
                      </div>
              
                      {/* Actions */}
                      <div className="pt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted small">
                            <i className="bi bi-clock-history me-1"></i>
                            Dernière modification: Aujourd'hui
                          </div>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="px-4 d-flex align-items-center gap-2 shadow-sm"
                          >
                            <i className="bi bi-check-lg"></i>
                            Sauvegarder
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
                              </>
                            )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de validation */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold d-flex align-items-center">
            <i className="bi bi-shield-check text-primary me-2"></i>
            Validation du compte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center">
          <div className="mb-4">
            <i className="bi bi-question-circle display-4 text-warning"></i>
          </div>
          <h5 className="fw-bold mb-3">Confirmer l'action</h5>
          <p className="text-muted">{validationMessage}</p>
          {selectedMember && (
            <Alert variant="info" className="small">
              <i className="bi bi-info-circle me-2"></i>
              Membre: <strong>{selectedMember.email}</strong> - 
              Statut actuel: <Badge bg={selectedMember.status === 'OK' || selectedMember.account_validation === 'OK' ? "success" : "warning"}>
                {selectedMember.status === 'OK' || selectedMember.account_validation === 'OK' ? 'Activé' : 'Désactivé'}
              </Badge>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowValidationModal(false)}
            className="px-4"
          >
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={validateUser}
            disabled={validatingUser}
            className="px-4"
          >
            {validatingUser ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Traitement...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Confirmer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold d-flex align-items-center">
            <i className="bi bi-exclamation-triangle text-danger me-2"></i>
            Suppression du compte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center">
          <div className="mb-4">
            <i className="bi bi-trash display-4 text-danger"></i>
          </div>
          <h5 className="fw-bold mb-3 text-danger">Attention !</h5>
          <p className="text-muted">{deleteMessage}</p>
          {selectedMember && (
            <Alert variant="danger" className="small">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Action irréversible :</strong> Le membre <strong>{selectedMember.email}</strong> sera définitivement supprimé du système.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
            className="px-4"
          >
            Annuler
          </Button>
          <Button 
            variant="danger" 
            onClick={deleteUser}
            disabled={deletingUser}
            className="px-4"
          >
            {deletingUser ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Suppression...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Supprimer définitivement
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Les autres modals restent inchangés */}
      <Modal show={showAddMemberModal} onHide={() => setShowAddMemberModal(false)} centered>
        {/* ... code modal inchangé ... */}
      </Modal>

      <Modal show={showEditOrgModal} onHide={() => setShowEditOrgModal(false)} size="lg" centered>
        {/* ... code modal inchangé ... */}
      </Modal>

      {/* === CSS INTERNE === */}
      <style>{`
        .tabs-vertical {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .tab-vertical {
          background: none;
          border: none;
          font-weight: 600;
          transition: all 0.3s ease;
          text-align: left;
        }
        .tab-vertical:hover {
          background-color: #f8f9fa;
          transform: translateX(5px);
        }
        .tab-vertical.active {
          background: linear-gradient(135deg, #198754, #20c997);
          color: white !important;
          box-shadow: 0 4px 15px rgba(25, 135, 84, 0.3);
          transform: translateX(5px);
        }
        .org-logo {
          width: 60px;
          height: 60px;
          font-size: 1.5rem;
        }
        .table > :not(caption) > * > * {
          padding: 1rem 0.75rem;
        }
        .bg-outline-primary {
          background-color: transparent !important;
          border: 1px solid #dee2e6 !important;
        }
        .shadow-sm-hover {
          transition: all 0.3s ease;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
        }
        .shadow-sm-hover:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
        }
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Footer />
    </div>
  );
}